/**
 * figure/chart 캡션 안에서 요소가 맡는 역할
 *
 * - type: "bar chart" 처럼 종류를 나타내는 배지
 * - description: 설명·OCR 텍스트 등 본문 성격의 문단
 */
type CaptionRole = "type" | "description";

const CAPTION_ROLE_STYLES: Record<CaptionRole, string[]> = {
  type: [
    "inline-block",
    "bg-muted-dark",
    "text-white",
    "px-2",
    "py-0.5",
    "rounded",
    "text-xs",
    "font-medium",
    "mb-2",
    // 캡션 값은 모델 표기를 그대로 오므로("Chart Type: bar", 구형 "bar chart") 표시만
    // 단어 첫 글자를 올린다. DOM 텍스트는 그대로 두어 복사·검색 결과가 원문과 같다.
    "capitalize",
  ],
  description: ["block", "text-sm", "leading-relaxed", "text-muted-foreground"],
};

/**
 * class 로 역할을 표시하는 현재 API 응답
 * (예: `<p class="chart-type">bar chart</p>`)
 */
const CAPTION_CLASS_ROLES: Record<string, CaptionRole> = {
  "chart-type": "type",
  "figure-type": "type",
  "chart-description": "description",
  "figure-description": "description",
  "chart-ocr-text": "description",
  "figure-ocr-text": "description",
};

/**
 * 커스텀 태그로 역할을 표시했던 legacy API 응답
 * (예: `<chart_type>bar chart</chart_type>`)
 */
const CAPTION_TAG_ROLES: Record<string, CaptionRole> = {
  chart_type: "type",
  chart_description: "description",
};

// class 기반 역할은 p/span 에만 적용한다 (table 등 다른 태그가 같은 class 를 달아도 무시)
const CAPTION_CLASS_TAGS = new Set(["p", "span"]);

/**
 * 태그 이름과 class 속성으로 캡션 역할을 판정한다
 *
 * @param tagName 원본 태그 이름 (소문자, 커스텀 태그 포함)
 * @param classAttribute 원본 class 속성 문자열
 */
export function getCaptionRole(
  tagName: string,
  classAttribute?: string,
): CaptionRole | undefined {
  const tagRole = CAPTION_TAG_ROLES[tagName];
  if (tagRole) {
    return tagRole;
  }

  if (!CAPTION_CLASS_TAGS.has(tagName)) {
    return undefined;
  }

  return (classAttribute ?? "")
    .split(/\s+/)
    .map(name => CAPTION_CLASS_ROLES[name])
    .find(Boolean);
}

/**
 * HTML 태그별 스타일 클래스를 반환하는 함수
 *
 * 캡션 역할(chart-type/chart-description 등)로 판정되면 태그 기본 스타일 대신
 * 역할 스타일만 적용한다. 배지의 text-xs 가 기본 text-sm 과 충돌하지 않게 하려는 것.
 */
export function getHtmlTagStyles(
  tagName: string,
  classAttribute?: string,
): string[] {
  const captionRole = getCaptionRole(tagName, classAttribute);
  if (captionRole) {
    return [...CAPTION_ROLE_STYLES[captionRole]];
  }

  const className: string[] = ["text-sm"];

  switch (tagName) {
    case "h1":
    case "h2":
    case "h3":
    case "h4":
    case "h5":
    case "h6":
    case "header":
      className.push("text-md", "font-semibold");
      break;

    case "footer":
    case "p":
      className.push("text-sm", "leading-6");
      break;

    case "figcaption":
    case "caption":
      className.push("text-sm", "text-muted-foreground", "my-4");
      break;

    case "table":
      className.push(
        "border-collapse",
        "[&_td]:p-2",
        "[&_td]:border",
        "[&_td]:border-border",
        "[&_td]:text-sm",
        "[&_th]:p-2",
        "[&_th]:border",
        "[&_th]:border-border",
        "[&_th]:bg-subtle",
        "[&_th]:text-left",
        "[&_th]:font-semibold",
        "[&_th]:text-sm",
      );
      break;

    case "ul":
    case "ol":
      className.push(
        tagName === "ul" ? "list-disc" : "list-decimal",
        "ml-6",
        "my-4",
        "space-y-1",
      );
      break;

    case "li":
      className.push("text-sm", "leading-6");
      break;

    case "a":
      className.push(
        "text-muted-foreground",
        "no-underline",
        "hover:underline",
      );
      break;

    case "strong":
      className.push("font-semibold");
      break;

    case "em":
      className.push("italic");
      break;

    case "code":
      className.push(
        "text-sm",
        "px-1.5",
        "py-0.5",
        "rounded",
        "font-mono",
        "bg-foreground",
        "text-background",
      );
      break;

    case "pre":
      className.push(
        "bg-subtle",
        "border",
        "border-border",
        "rounded-lg",
        "p-4",
        "overflow-x-auto",
        "my-4",
        // code 블록 안에서는 개행·들여쓰기가 곧 의미이므로 그대로 보존한다.
        "whitespace-pre",
        // <code> 는 인라인 배지 스타일(반전 박스)이라 <pre> 안에 중첩되면 블록 전체가
        // 반전된다. 직계 <code> 의 배지 스타일을 리셋해 코드 블록으로 렌더되게 한다.
        "[&>code]:bg-transparent",
        "[&>code]:text-inherit",
        "[&>code]:p-0",
        "[&>code]:rounded-none",
      );
      break;

    case "blockquote":
      className.push(
        "border-l-4",
        "border-border",
        "pl-4",
        "italic",
        "text-muted-foreground",
        "my-4",
      );
      break;

    case "hr":
      className.push("border-border", "my-4", "border-t");
      break;

    case "img":
      className.push("rounded-lg", "my-4", "max-w-full", "h-auto");
      break;

    default:
      break;
  }

  return className;
}
